import { Form, FormikProvider, useFormik } from 'formik';
import { camelCase } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import * as Yup from 'yup';

import { QuestionMarkIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Callout,
	Heading3,
	Input,
	Label,
	LanguageSelect,
	Modal,
	Paragraph,
	Select,
	Separator,
} from '@treams/treams-ui';

const WORKSPACE_POSTFIXES_WITHOUT_HUBSPOT = [
	'demo',
	'test',
	'treams',
	'onboarding',
	'lizannedv',
	'acceptance',
	'engagement',
] as const;

export function isHubspotRequired(workspaceId: string): boolean {
	const id = workspaceId ?? '';

	return !WORKSPACE_POSTFIXES_WITHOUT_HUBSPOT.some((postfix) => id.includes(postfix));
}

// https://gist.github.com/max10rogerio/c67c5d2d7a3ce714c4bc0c114a3ddc6e
const slugify = (value: string): string => {
	return value
		.normalize('NFD') // split an accented letter in the base letter and the acent
		.replace(/[\u0300-\u036f]/g, '') // remove all previously split accents
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9 ]/g, '') // remove all chars not letters, numbers and spaces (to be replaced)
		.replace(/\s+/g, '-'); // separator
};

export default function WorkspaceCreateModal(props: { onDismiss: () => void }) {
	const { t } = useTranslation();

	const [create] = graphql.useCreateWorkspaceMutation();
	const [checkAvailable] = graphql.useIsWorkspaceSlugAvailableLazyQuery();

	const [planType, setPlanType] = useState(graphql.WorkspacePlanType.Pro);
	const [locale, setLocale] = useState('nl_NL');

	const formik = useFormik({
		onSubmit: async (values) => {
			const input: graphql.CreateWorkspaceInput = {
				workspaceID: values.workspaceID,
				name: values.name,
				hubSpotCompanyID: values.hubSpotCompanyID ?? '',
				customer: {
					firstName: values.firstName,
					lastName: values.lastName,
					email: values.email,
					username: slugify(values.firstName + ' ' + values.lastName),
					locale,
				},
				planType,
				isOnboarding: true,
			};

			await create({
				variables: {
					input,
				},
			});

			props.onDismiss();
		},
		initialValues: {
			workspaceID: '',
			name: '',
			hubSpotCompanyID: '',
			firstName: '',
			lastName: '',
			email: '',
		},
		validationSchema: Yup.object({
			workspaceID: Yup.string()
				.matches(/^[a-z0-9](-?[a-z0-9])*$/, t('workspaces.form.workspaceIDInvalid'))
				.test('available', t('workspaces.form.workspaceIDTaken'), async (value) => {
					if (!value) {
						return false;
					}

					const result = await checkAvailable({
						variables: {
							workspaceID: value,
						},
					});

					return result.data?.isWorkspaceSlugAvailable ?? false;
				})
				.required()
				.min(3),
			hubSpotCompanyID: Yup.string().when('workspaceID', ([workspaceID], schema) => {
				const id = typeof workspaceID === 'string' ? workspaceID : '';
				const required = isHubspotRequired(id);
				return required
					? schema.required(t('workspaces.form.hubSpotCompanyIDRequired')).min(7)
					: schema.notRequired();
			}),
			name: Yup.string().required().min(3),
			firstName: Yup.string().required().min(1),
			lastName: Yup.string().required().min(1),
			email: Yup.string().email().required(),
		}).required(),
	});

	return (
		<Modal width={600}>
			<Modal.Header title={t('common.settings.workspaces.create.title')} tabs />
			<Modal.Contents>
				<FormikProvider value={formik}>
					<Form>
						<Heading3 mb={3}>{t('workspaces.form.company')}</Heading3>

						<Flex>
							<Box mb={3} width={1 / 2} mr={3}>
								<Label mb={2}>{t('workspaces.form.companyName')}</Label>
								<Input.Formik name="name" validate />
							</Box>

							<Box mb={3} width={1 / 2}>
								<Label mb={2}>{t('workspaces.form.workspaceID')}</Label>
								<Input.Formik name="workspaceID" validate />
								{formik.errors.workspaceID && (
									<Paragraph color="red" mt={2}>
										{formik.errors.workspaceID}
									</Paragraph>
								)}
							</Box>
						</Flex>

						<Box mb={3}>
							<Label mb={2}>{t('settings.workspaces.hubspotCompanyID.label')}</Label>
							<Input.Formik name="hubSpotCompanyID" validate />
							{formik.errors.hubSpotCompanyID && (
								<Paragraph color="red" mt={2}>
									{formik.errors.hubSpotCompanyID}
								</Paragraph>
							)}
						</Box>

						<Callout
							description={t('workspaces.form.slugExplanation')}
							icon={<QuestionMarkIcon />}
							color="blue"
						/>

						<Separator my={4} />

						<Heading3 mb={3}>{t('workspaces.form.superuser')}</Heading3>

						<Flex>
							<Box mb={3} width={1 / 2} mr={3}>
								<Label mb={2}>{t('workspaces.form.firstName')}</Label>
								<Input.Formik name="firstName" validate />
							</Box>

							<Box mb={3} width={1 / 2}>
								<Label mb={2}>{t('workspaces.form.lastName')}</Label>
								<Input.Formik name="lastName" validate />
							</Box>
						</Flex>

						<Box mb={3}>
							<Label mb={2}>{t('workspaces.form.email')}</Label>
							<Input.Formik name="email" type="email" validate />
						</Box>

						<Separator my={4} />

						<Flex>
							<Box mb={3} width={1 / 2} mr={3}>
								<Label mb={2}>{t('workspaces.form.language')}</Label>
								<LanguageSelect value={locale} onChange={setLocale} />
							</Box>

							<Box mb={3} width={1 / 2}>
								<Label mb={2}>{t('settings.workspaces.edit.plan.title')}</Label>
								<Select
									tint={false}
									search={false}
									selectedValue={planType}
									selectedOption={{
										title: t(`workspaces.plans.type.${camelCase(planType)}`),
										value: planType,
									}}
									options={Object.keys(graphql.WorkspacePlanType).map((typeKey) => ({
										title: t(
											`workspaces.plans.type.${camelCase(graphql.WorkspacePlanType[typeKey])}`,
										),
										value: graphql.WorkspacePlanType[typeKey],
									}))}
									onChange={(value) => setPlanType(value as graphql.WorkspacePlanType)}
								/>
							</Box>
						</Flex>
					</Form>
				</FormikProvider>
			</Modal.Contents>
			<Modal.FormFooter
				small
				onCancel={() => props.onDismiss()}
				onSave={formik.submitForm}
				saveTitle={t('common.save')}
				disabled={!formik.isValid}
			/>
		</Modal>
	);
}
